import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import WPAgGrid from "../../packages/WPAgGrid/WPAgGrid";
import WPButton from "../../packages/WPButton";
import WPPagination from "../../packages/WPPagination";
import { useToastNotification } from "../../packages/WPToastNotification/WPToastNotificationProvider";

import RequestStatusFilterHeader from "../../components/AdminPortal/customFilter";
import Header from "../../components/Header";

import { DEFAULT_PAGE_SIZE_OPTION } from "../../constants/global.constants";

import Footer from "../../common/footer/Footer";
import CircularLoader from "../../common/styles/components/circularLoader";

import {
  downloadBlobData,
  formatToLocalDateTime,
  getRequestStatusText,
} from "../../utils/Utils";

import {
  exportExcel,
  getAccessRequest,
  updateRequests,
} from "../../network/cipAPI";

import "./index.scss";

const AdminPage = () => {
  const { t } = useTranslation();
  const { token } = useSelector((state) => state);
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();

  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_OPTION);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      // Export type set to 'request_access'
      const type = "request_access";
      const response = await exportExcel(type);
      if (response && response.data) {
        downloadBlobData(
          response.data,
          t("adminPage.accessRequestsFile"),
          response.headers,
        );
        showSuccessNotification(
          t("notificationStrings.tableDataExportSuccess"),
        );
      }
    } catch (error) {
      showErrorNotification(t("notificationStrings.tableDataExportError"));
    } finally {
      setExportLoading(false);
    }
  };

  const fetchAccessRequests = async () => {
    setShowOverLayLoader(true);
    try {
      const response = await getAccessRequest(
        currentPage,
        pageSize,
        currentFilters,
        sortField,
        sortOrder,
      );
      if (response && response.data) {
        setRowData(response.data.users || []);
        setTotalRequests(response.data.total_users || 0);
      } else {
        setRowData([]);
        setTotalRequests(0);
      }
    } catch (error) {
      setRowData([]);
      setTotalRequests(0);
      showErrorNotification(t("notificationStrings.somethingWrong"));
    } finally {
      setShowOverLayLoader(false);
    }
  };

  useEffect(() => {
    if (token) fetchAccessRequests();
  }, [currentPage, pageSize, currentFilters, sortField, sortOrder, token]);

  const onSortChanged = (event) => {
    const sortModel = event.columnApi
      .getAllColumns()
      .map((column) => {
        return {
          colId: column.getColId(),
          sort: column.getSort(),
        };
      })
      .filter((col) => col.sort);

    if (sortModel && sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      setSortField(colId);
      setSortOrder(sort);
    } else {
      setSortField("created_at");
      setSortOrder("desc");
    }
  };

  const StatusButtonsCellRenderer = (props) => {
    const { request_status, email } = props.data;
    const handleButtonClick = async (requestStatus) => {
      const payload = {
        user_email: email,
        request_status: requestStatus,
        comments: props.data.comments,
      };
      setShowOverLayLoader(true);
      try {
        await updateRequests(payload);
        fetchAccessRequests();
      } catch (error) {
        showErrorNotification(t("notificationStrings.somethingWrong"));
      } finally {
        setShowOverLayLoader(false);
      }
    };
    return (
      <div className="status-buttons">
        {(request_status === 1 || request_status === 4) && (
          <>
            <WPButton onClick={() => handleButtonClick(2)}>
              {t("adminPage.accept")}
            </WPButton>
            <WPButton type="danger" onClick={() => handleButtonClick(3)}>
              {t("adminPage.reject")}
            </WPButton>
          </>
        )}
        {(request_status === 2 || request_status === 3) && (
          <WPButton type="warning" onClick={() => handleButtonClick(4)}>
            {t("adminPage.revoke")}
          </WPButton>
        )}
      </div>
    );
  };

  const onRequestStatusFilterChange = (filterQuery) => {
    const filters = filterQuery ? filterQuery.split(",") : [];
    setCurrentPage(1);
    setCurrentFilters(filters);
  };

  const SMALL_COLUMN_FLEX = 0.5;
  const DEFAULT_COLUMN_FLEX = 1;

  const colDefs = [
    {
      headerName: t("adminPage.userid"),
      field: "id",
      flex: SMALL_COLUMN_FLEX,
      minWidth: 80,
      sortable: false,
    },
    {
      headerName: t("adminPage.userName"),
      field: "name",
      flex: DEFAULT_COLUMN_FLEX,
      minWidth: 150,
      sortable: false,
    },
    {
      headerName: t("adminPage.company"),
      field: "company",
      flex: DEFAULT_COLUMN_FLEX,
      minWidth: 150,
      sortable: false,
    },
    {
      headerName: t("adminPage.country"),
      field: "country",
      flex: DEFAULT_COLUMN_FLEX,
      minWidth: 100,
      sortable: false,
    },
    {
      headerName: t("adminPage.email"),
      field: "email",
      flex: DEFAULT_COLUMN_FLEX,
      minWidth: 250,
      sortable: false,
    },
    {
      headerName: t("adminPage.created_at"),
      field: "created_at",
      flex: DEFAULT_COLUMN_FLEX,
      minWidth: 180,
      sortable: true,
      sortingOrder: ["desc", "asc"],
      sort: sortOrder,
      valueFormatter: (params) => {
        return formatToLocalDateTime(params.value);
      },
    },
    {
      headerName: t("adminPage.request_status"),
      field: "request_status",
      flex: DEFAULT_COLUMN_FLEX,
      minWidth: 150,
      sortable: false,
      headerComponent: RequestStatusFilterHeader,
      headerComponentParams: {
        onFilterChange: onRequestStatusFilterChange,
        currentFilters: currentFilters,
      },
      valueGetter: (params) =>
        getRequestStatusText(t, params.data.request_status),
    },
    {
      headerName: t("adminPage.comments"),
      field: "comments",
      flex: DEFAULT_COLUMN_FLEX,
      minWidth: 150,
      sortable: false,
      cellEditorPopup: true,
      editable: true,
      cellEditor: "agLargeTextCellEditor",
      cellEditorParams: {
        maxLength: 100,
      },
      cellStyle: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
      },
      tooltipValueGetter: (params) => {
        return params.value;
      },
    },
    {
      headerName: t("adminPage.actions"),
      field: "actions",
      width: 200,
      cellRenderer: StatusButtonsCellRenderer,
      sortable: false,
    },
  ];

  const onCellValueChanged = async (event) => {
    const { data } = event;
    const payload = {
      user_email: data.email,
      request_status: data.request_status,
      comments: data.comments,
    };
    setShowOverLayLoader(true);
    try {
      await updateRequests(payload);
      fetchAccessRequests();
    } catch (error) {
      showErrorNotification(t("notificationStrings.somethingWrong"));
    } finally {
      setShowOverLayLoader(false);
    }
  };

  const handlePageChange = (page, newPageSize) => {
    if (newPageSize && newPageSize !== pageSize) {
      setCurrentPage(1);
      setPageSize(newPageSize);
      return;
    }
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="container-main">
      {showOverLayLoader && (
        <div className="circular-loader-overlay">
          <CircularLoader />
        </div>
      )}
      <Header />
      <div className="main-container">
        <div className="content-container">
          <div className="admin-title">
            <span>{t("adminPage.manageAcess")}</span>
            <WPButton onClick={handleExportExcel} disabled={exportLoading}>
              {exportLoading
                ? t("adminPage.exporting")
                : t("adminPage.exportExcel")}
            </WPButton>
          </div>
          <div className="descriptionText marginBtm10">
            {t("adminPage.createdAtInfoText")}
          </div>

          <div className="ro-ag-table">
            <WPAgGrid
              columnDefs={colDefs}
              rowData={rowData}
              tableHeight="95vh"
              onSortChanged={onSortChanged}
              onCellValueChanged={onCellValueChanged}
            />
            <WPPagination
              totalItems={totalRequests}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default AdminPage;
